/* eslint-disable @typescript-eslint/no-explicit-any */
import ProductGiftList from '@/components/ProductDetailPage/ProductGiftList'
import ProductInformation from '@/components/ProductDetailPage/ProductInformation'
import ProductReviewAndRate from '@/components/ProductDetailPage/ProductReviewAndRate'
import ProductServiceList from '@/components/ProductDetailPage/ProductServiceList'
import ProductSpecifications from '@/components/ProductDetailPage/ProductSpecifications'
import { ProductStockStatus } from '@/constants'
import { useAuth, useCart, useStore } from '@/containers'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import type { GiftItems, OtherOffers, ProductDetail } from '@/types'
import { type OrderService } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { stripHtmlTagsAndLimit } from '@/utils/next-util'
import orderBy from 'lodash/orderBy'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import Breadcrumb from '../Breadcrumb'
import HomePageBlogListWrapper from '../HomePageBlogList/HomePageBlogListWrapper'
import ProductActions from './ProductActions'
import ProductCreateReviewModal from './ProductCreateReviewModal'
import ProductDetailHeader from './ProductDetailHeader'
import ProductDetailPrice from './ProductDetailPrice'
import ProductGallery from './ProductGallery'
import ProductModal from './ProductModal'

const ModalVoucherPickUp = dynamic(() => import('./ModalVoucherPickUp'), {
  ssr: false,
})

const RelatedProduct = dynamic(() => import('./RelatedProduct'), {
  ssr: false,
})

const ViewedProductListWrapper = dynamic(
  () => import('../ViewedProductList').then(m => m.ViewedProductListWrapper),
  {
    ssr: false,
  }
)

interface DynamicPageProps {
  data: ProductDetail
}

const ProductDetailPage = ({ data }: DynamicPageProps) => {
  const router = useRouter()
  const [listService, setListService] = useState<OrderService[]>([])
  const [listGift, setListGift] = useState<GiftItems[] | undefined>([])
  const [listOffer, setListOffer] = useState<OtherOffers[] | undefined>([])
  const [pickUpInStore, setPickUpInStore] = useState(false)
  const [priceWithService, setPriceWithService] = useState(0)
  const t = useTranslations()

  const { channel } = useAuth()
  const { addToCart, setIsPreOrder } = useCart()
  const {
    productCompares,
    setProductCompares,
    setIsOpenComparePopup,
    setProductViewed,
    setCatViewId,
  } = useStore()
  const { isPreOrder } = useCart()
  const productName = useMemo(() => {
    return getTranslate(data.name)
  }, [data])

  const productDescription = useMemo(() => {
    return stripHtmlTagsAndLimit(getTranslate(data.description), 160)
  }, [data])

  const pageShortDescription = useMemo(() => {
    return stripHtmlTagsAndLimit(getTranslate(data.shortDescription), 160)
  }, [data])

  useEffect(() => {
    if (data.preOrder) {
      return setIsPreOrder(true)
    }
    return setIsPreOrder(false)
  }, [data])

  useEffect(() => {
    if (data) {
      const newListService =
        data?.orderServices
          ?.sort((a: any, b: any) => a?.order - b?.order)
          .map((e: any) => {
            if (e) {
              return { ...e, active: false }
            }
            return null
          })
          .filter(Boolean) || []
      setListService(newListService)
    }
  }, [data, channel])

  useEffect(() => {
    if (data) {
      setCatViewId(data?.catId)
      setProductViewed(
        data?.relatedProductIds.map((e: any) => ({
          _id: e,
        }))
      )
      const mergeData = data.gifts?.reduce((result: any, item: any) => {
        result.otherOffers = (result.otherOffers || []).concat(item.otherOffers)
        result.giftItems = (result.giftItems || []).concat(item.giftItems)
        return result
      }, {} as any)
      const gifts = mergeData?.giftItems?.map((e: any) => {
        return { ...e, active: true }
      })
      return setListGift(gifts)
    }
  }, [data, channel])

  useEffect(() => {
    if (data) {
      const mergeData = data.gifts?.reduce((result: any, item: any) => {
        result.otherOffers = (result.otherOffers || []).concat(item.otherOffers)
        return result
      }, {} as any)
      const offer = mergeData?.otherOffers?.map((e: any) => {
        return { content: e, active: true }
      })
      if (
        data.gifts?.filter((item: { otherOffers: any }) => item.otherOffers)
          .length > 0
      ) {
        return setListOffer(offer)
      }
      return () => {
        setIsOpenComparePopup(false)
      }
    }
  }, [data, channel])

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      const element = document.querySelectorAll('section[id]')
      if (element !== null) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        element.forEach(function (section: any) {
          const sectionTop = section?.offsetTop - 64
          const sectionBottom = sectionTop + section?.offsetHeight
          if (scrollPos >= sectionTop - 64 && scrollPos < sectionBottom + 64) {
            document.querySelectorAll('.link-section').forEach(function (link) {
              if (link.getAttribute('data-name') === '#' + section.id) {
                link.classList.add('active')
              } else {
                link.classList.remove('active')
              }
            })
          }
        })
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const [isOpenVoucher, setIsOpenVoucher] = useState(false)

  useEffect(() => {
    const isApplyVoucher = router?.query?.voucher
    if (isApplyVoucher) {
      setIsOpenVoucher(true)
      localStorage.setItem('modalApplyVoucher', '1')
    }
  }, [router])

  const closeModalApplyVoucher = () => {
    localStorage.removeItem('modalApplyVoucher')
    setIsOpenVoucher(false)
    const currentPath = router.pathname
    const query = { ...router.query }
    delete query.voucher
    router.replace(
      {
        pathname: currentPath,
        query: query,
      },
      undefined,
      { shallow: true }
    )
  }
  const defaultReviewSummary = [0, 0, 0, 0, 0]
  const reviewSummaryData = useMemo(() => {
    if (data.reviewSummary) {
      return data.reviewSummary
    }
    return defaultReviewSummary
  }, [data])

  const isOutOfStock = useMemo(() => {
    if (
      [
        ProductStockStatus.OUT_OF_STOCK,
        ProductStockStatus.OUT_OF_BUSINESS,
      ].includes(data.isOutOfStock) &&
      !data.flashSale
    ) {
      return true
    }
    return false
  }, [data])

  const [modalIndex, setModalIndex] = useState<number | null>(null)

  const [showModalCreateReview, setShowModalCreateReview] = useState(false)
  const { token } = useAuth()

  const handleShowCreateReview = () => {
    if (!token) {
      return setShowModalCreateReview(true)
    }

    router.push(`/login?&redirect=${router.asPath}`)
  }
  return (
    <Main
      meta={
        <Meta
          title={
            getTranslate(data.metaTitle) && getTranslate(data.metaTitle) !== ''
              ? getTranslate(data.metaTitle)
              : getTranslate(data.title)
          }
          description={
            getTranslate(data.metaDescription) &&
            getTranslate(data.metaDescription) !== ''
              ? getTranslate(data.metaDescription)
              : pageShortDescription || productDescription || ''
          }
          noIndex={data.noIndex}
          noFollow={data.noFollow}
          keywords={getTranslate(data.keywords) || []}
          imageUrl={data.thumbnail}
          canonical={
            data.canonicalUrl ||
            process.env.NEXT_PUBLIC_URL_WEBSITE + router.asPath
          }
          mobileAlternate={{
            media: 'only screen and (max-width:991px))',
            href:
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE + router.asPath ||
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE ||
              '',
          }}
        />
      }
      page='product-detail'
      bgColor='!bg-white'
    >
      <div className='flex gap-1 py-5'>
        <Breadcrumb
          translateText={t('ProductDetail.Homepage')}
          breadcrumb={data.cat}
        />
        <span>/</span>
        <span>{productName}</span>
      </div>
      <div className='mt-5 flex gap-16'>
        <div className='w-[55%]'>
          <ProductGallery
            alt={productName}
            product={data}
            onClickBottomItem={(index: number) => setModalIndex(index)}
          />
          <div className='mt-5'>
            <ProductSpecifications
              specifications={data.properties}
              onViewAll={() => setModalIndex(1)}
            />
          </div>
          <ProductInformation
            description={data.description}
            className='mb-3 mt-5 border-t !p-0 !pt-5'
          />
        </div>
        <div className='w-[45%]'>
          <ProductDetailHeader
            entrySummaryData={data}
            productCompares={productCompares}
            setProductCompares={setProductCompares}
            setIsOpenComparePopup={setIsOpenComparePopup}
          />
          <ProductDetailPrice
            entrySummaryData={data}
            listService={listService}
            listGift={listGift}
            listOffer={listOffer}
            pickUpInStore={pickUpInStore}
            isOutOfStock={isOutOfStock}
            bonusAmount={data.bonusAmount}
          />
          {(!!listGift?.length || !!listService?.length) && (
            <ProductGiftList
              entrySummaryData={data}
              listGift={listGift}
              listOffer={listOffer}
              className='mt-3'
            />
          )}
          {!!listService?.length && (
            <ProductServiceList
              entrySummaryData={data}
              setListService={setListService}
              listService={listService}
              pickUpInStore={pickUpInStore}
              setPickUpInStore={setPickUpInStore}
              priceWithService={priceWithService}
              setPriceWithService={setPriceWithService}
              className='mt-2'
            />
          )}

          <ProductActions
            entrySummaryData={data}
            handleAddToCart={addToCart}
            isOutOfStock={isOutOfStock}
          />

          <ProductReviewAndRate
            productId={data._id}
            average={data.average}
            reviewSummary={reviewSummaryData}
            productName={getTranslate(data.name)}
            className='mt-8 border-t'
            onViewAll={() => setModalIndex(2)}
            onOpenCreateModal={() => handleShowCreateReview()}
            hideListIfEmpty
          />
        </div>
      </div>

      <RelatedProduct
        relatedProductData={orderBy(
          data?.relatedProducts || [],
          'order',
          'asc'
        )}
        className='mx-2 mt-5 bg-[#F3F4F6]'
      />

      <ViewedProductListWrapper className='-mx-1 mt-3 bg-[#f3f4f6] py-5' />

      <HomePageBlogListWrapper />

      {data.coupons && (
        <ModalVoucherPickUp
          isOpen={isOpenVoucher}
          setIsOpen={() => closeModalApplyVoucher()}
          data={data}
          listGift={listGift}
          listService={listService}
          isPreOrder={isPreOrder}
          pickUpInStore={pickUpInStore}
          listOffer={listOffer}
          addToCartData={data}
        />
      )}

      {modalIndex !== null && (
        <ProductModal
          onClose={() => setModalIndex(null)}
          defaultIndex={modalIndex}
          open={modalIndex !== null && !showModalCreateReview}
          average={data.average}
          productDetail={data}
          productName={productName}
          reviewSummary={reviewSummaryData}
          onOpenCreateModal={handleShowCreateReview}
        />
      )}

      <ProductCreateReviewModal
        showModalCreateReview={showModalCreateReview}
        setShowModalCreateReview={setShowModalCreateReview}
        productName={productName}
        productId={data?._id}
      />
    </Main>
  )
}

export default ProductDetailPage
